import { useCallback, useEffect, useState } from 'react';
import { MonitorSmartphone } from 'lucide-react';
import {
  fetchMySessions,
  revokeOtherSessions,
  type MySessionsOutcome,
  type RevokeOthersOutcome,
} from '../../lib/api/mySessions';
import { useT } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// «INNLOGGINGER» — how many other places this account is signed in, and
// «Logg ut alle andre» (#1141, AUD-017).
//
// Since #1091 a sign-in can be remembered for thirty days, and a lost phone
// stayed signed in until support disabled the account. This is the person's
// own way out. Beside the passkeys, for the same reason they are there: it is
// about the account, not the player.
//
// CONFIRMED IN PLACE, never `window.confirm()` (DOMAIN_RULES). The action needs
// a sign-in from the last fifteen minutes; the transport opens the step-up
// prompt on that refusal, and this says why nothing happened yet.
// ─────────────────────────────────────────────────────────────────────

export function SessionsSection() {
  const t = useT();
  const [list, setList] = useState<MySessionsOutcome | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RevokeOthersOutcome | null>(null);

  const refresh = useCallback(() => {
    void fetchMySessions().then(setList);
  }, []);
  useEffect(refresh, [refresh]);

  const signOutOthers = async () => {
    if (busy) return;
    setConfirming(false);
    setBusy(true);
    const outcome = await revokeOtherSessions();
    setBusy(false);
    setResult(outcome);
    if (outcome.status === 'revoked') refresh();
  };

  const resultText = (r: RevokeOthersOutcome): { text: string; tone: 'good' | 'bad' } => {
    switch (r.status) {
      case 'revoked':
        return { text: t('account.sessions.done', { count: r.count }), tone: 'good' };
      case 'reauthentication_required':
        return { text: t('account.sessions.stepUp'), tone: 'bad' };
      default:
        return { text: t('account.sessions.failed'), tone: 'bad' };
    }
  };

  const others = list?.status === 'ok' ? list.others : null;

  return (
    <section className="card p-5" data-testid="sessions-section">
      <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
        <MonitorSmartphone size={18} /> {t('account.sessions')}
      </h2>
      {list === null && <p className="mt-2 text-sm text-ink-3">{t('account.sessions.loading')}</p>}
      {list?.status === 'unreachable' && (
        <p className="mt-2 text-sm text-danger">{t('account.sessions.listFailed')}</p>
      )}
      {others !== null && (
        <p className="mt-2 text-sm text-ink-2" data-testid="sessions-count">
          {others === 0
            ? t('account.sessions.onlyHere')
            : t('account.sessions.elsewhere', { count: others })}
        </p>
      )}

      {result && (
        <p
          role="status"
          aria-live="polite"
          className={`mt-2 text-sm ${resultText(result).tone === 'good' ? 'text-ink-2' : 'text-danger'}`}
        >
          {resultText(result).text}
        </p>
      )}

      {others !== null && others > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {confirming ? (
            <>
              <span className="text-sm text-ink-2">{t('account.sessions.confirm')}</span>
              <button
                type="button"
                className="text-sm font-semibold text-danger underline"
                disabled={busy}
                onClick={() => void signOutOthers()}
                data-testid="sessions-confirm"
              >
                {t('account.sessions.yes')}
              </button>
              <button
                type="button"
                className="text-sm text-ink-3 underline"
                onClick={() => setConfirming(false)}
              >
                {t('account.sessions.no')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => {
                setResult(null);
                setConfirming(true);
              }}
              data-testid="sessions-sign-out-others"
            >
              {busy ? t('account.sessions.working') : t('account.sessions.signOutOthers')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
